<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useLayout } from "@/layout/composables/layout";
import { addDoc, collection, doc, getDoc, serverTimestamp, onSnapshot, updateDoc, orderBy, limit, query } from "firebase/firestore";
import { useToast } from "vue-toastification";
import { db } from "@/firebase";

const store = useStore();
const router = useRouter();
const { onMenuToggle } = useLayout();
const toast = useToast();

const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);

// Kiosk mode
const isKiosk = computed(() => localStorage.getItem("deviceKiosk") === "true");
let idleTimeoutMs = 5 * 60 * 1000; // default 5 minutes, overridden by Firestore
const IDLE_WARNING_SECONDS = 10;
let idleTimer = null;
let warningTimer = null;
let countdownInterval = null;
const idleCountdown = ref(0);
const showIdleWarning = ref(false);
const idleEvents = ["mousedown", "mousemove", "keydown", "touchstart", "scroll"];

const fetchIdleTimeout = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, "auctionSettings", "kioskSettings"));
    if (settingsDoc.exists() && settingsDoc.data().idleTimeoutSeconds !== undefined) {
      const seconds = settingsDoc.data().idleTimeoutSeconds;
      idleTimeoutMs = seconds > 0 ? seconds * 1000 : 0;
    }
  } catch (error) {
    console.error("Error fetching kiosk idle timeout:", error);
  }
};

const clearIdleWarning = () => {
  showIdleWarning.value = false;
  idleCountdown.value = 0;
  clearTimeout(warningTimer);
  clearInterval(countdownInterval);
};

const startWarningCountdown = () => {
  showIdleWarning.value = true;
  idleCountdown.value = IDLE_WARNING_SECONDS;
  countdownInterval = setInterval(() => {
    idleCountdown.value--;
    if (idleCountdown.value <= 0) {
      clearInterval(countdownInterval);
    }
  }, 1000);
};

const resetIdleTimer = () => {
  if (!isKiosk.value || !idleTimeoutMs) return;
  clearTimeout(idleTimer);
  clearTimeout(warningTimer);
  clearIdleWarning();

  const warningMs = idleTimeoutMs - IDLE_WARNING_SECONDS * 1000;

  if (warningMs > 0) {
    warningTimer = setTimeout(() => {
      startWarningCountdown();
    }, warningMs);
  }

  idleTimer = setTimeout(() => {
    clearIdleWarning();
    switchUser();
  }, idleTimeoutMs);
};

const startIdleWatcher = () => {
  if (!idleTimeoutMs) return;
  idleEvents.forEach((evt) => document.addEventListener(evt, resetIdleTimer, { passive: true }));
  resetIdleTimer();
};

const stopIdleWatcher = () => {
  clearTimeout(idleTimer);
  clearTimeout(warningTimer);
  clearInterval(countdownInterval);
  showIdleWarning.value = false;
  idleEvents.forEach((evt) => document.removeEventListener(evt, resetIdleTimer));
};

const showHelpDialog = ref(false);
const sendingHelp = ref(false);
const helpType = ref("Bid Support");
const helpMessage = ref("");

const helpDialogVisible = ref(false);
const helpRequests = ref([]);
let unsubscribeHelpRequests = null;

const openHelpRequests = computed(() =>
  helpRequests.value.filter((request) => request.status === "open")
);

const openHelpCount = computed(() => openHelpRequests.value.length);

const getRequestSummary = (request) => {
  const tableLabel = request.requesterTableName || request.requesterTableId || "Unknown table";
  const nameLabel = request.requesterName || "Unknown user";
  return `${tableLabel} - ${nameLabel} needs help`;
};

const formatRequestTimestamp = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return "Now";
  const date = new Date(timestamp.seconds * 1000);
  return date.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const markHelpResolved = async (requestId) => {
  try {
    await updateDoc(doc(db, "helpRequests", requestId), {
      status: "resolved",
      resolvedAt: serverTimestamp(),
      resolvedBy: displayName.value,
    });
    toast.success("Help request marked as resolved.");
  } catch (error) {
    toast.error("Could not update help request.");
  }
};

const subscribeToHelpRequests = () => {
  const helpQuery = query(
    collection(db, "helpRequests"),
    orderBy("createdAt", "desc"),
    limit(50)
  );

  unsubscribeHelpRequests = onSnapshot(helpQuery, (snapshot) => {
    helpRequests.value = snapshot.docs.map((requestDoc) => ({
      id: requestDoc.id,
      ...requestDoc.data(),
    }));
  });
};


const userProfile = computed(
  () => store.getters["user"].data || store.getters["customUser"] || {}
);
const isUserAuthenticated = computed(() => store.getters.isAuthenticated);
const userIsAdmin = computed(() => store.getters.userIsAdmin);

const displayName = computed(() => {
  const user = userProfile.value || {};
  const first = user.firstName || "";
  const last = user.lastName || "";
  const full = `${first} ${last}`.trim();
  return full || user.name || "Inloggad anv\u00E4ndare";
});

const signOut = async () => {
  await store.dispatch("logOut");
  router.push("/");
};

const switchUser = async () => {
  const tableId = userProfile.value?.assignedTable || "";
  await store.dispatch("logOut");
  if (tableId) {
    router.push({ path: "/", query: { tableId } });
  } else {
    router.push("/");
  }
};

const openHelpDialog = () => {
  helpType.value = "Bid Support";
  helpMessage.value = "";
  showHelpDialog.value = true;
};

const closeHelpDialog = () => {
  showHelpDialog.value = false;
  sendingHelp.value = false;
};

const submitHelpRequest = async () => {
  if (!isUserAuthenticated.value || sendingHelp.value) return;

  const user = userProfile.value || {};
  const tableId = user.assignedTable || user.tableId || "";
  let tableName = user.assignedTableName || user.table || localStorage.getItem("deviceTableName") || "";
  const userName = displayName.value;

  sendingHelp.value = true;

  try {
    if (!tableName && tableId) {
      const tableDoc = await getDoc(doc(db, "tables", tableId));
      if (tableDoc.exists()) {
        tableName = tableDoc.data().name || tableId;
      }
    }

    if (!tableName) {
      tableName = tableId || "Unknown table";
    }

    const summary = `${tableName} - ${userName} needs help`;

    await addDoc(collection(db, "helpRequests"), {
      status: "open",
      summary,
      message: helpMessage.value.trim() || "",
      requesterUserId: user.id || store.getters.userDocId || "",
      requesterName: userName,
      requesterTableId: tableId,
      requesterTableName: tableName,
      requesterMobile: user.mobile || null,
      source: "auction_topbar",
      createdAt: serverTimestamp(),
    });

    toast.success("Help request sent to admin.");
    closeHelpDialog();
  } catch (error) {
    toast.error("Could not send help request.");
  } finally {
    sendingHelp.value = false;
  }
};

onMounted(async () => {
  bindOutsideClickListener();
  if (userIsAdmin.value) {
    subscribeToHelpRequests();
  }
  if (isKiosk.value) {
    await fetchIdleTimeout();
    startIdleWatcher();
  }
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
  if (unsubscribeHelpRequests) {
    unsubscribeHelpRequests();
  }
  stopIdleWatcher();
});

const onTopBarMenuButton = () => {
  topbarMenuActive.value = !topbarMenuActive.value;
};

const onSettingsClick = () => {
  topbarMenuActive.value = false;
  router.push({ name: "Edit User Info" });
};

const topbarMenuClasses = computed(() => ({
  "layout-topbar-menu-mobile-active": topbarMenuActive.value,
}));

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        topbarMenuActive.value = false;
      }
    };
    document.addEventListener("click", outsideClickListener.value);
  }
};

const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener("click", outsideClickListener.value);
    outsideClickListener.value = null;
  }
};

const isOutsideClicked = (event) => {
  if (!topbarMenuActive.value) return false;

  const sidebarEl = document.querySelector(".layout-topbar-menu");
  const topbarEl = document.querySelector(".layout-topbar-menu-button");

  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  );
};
</script>

<template>
  <div class="layout-topbar">
    <button class="p-link layout-menu-button layout-topbar-button" @click="onMenuToggle()">
      <i class="pi pi-bars"></i>
    </button>

    <button class="p-link layout-topbar-menu-button layout-topbar-button" @click="onTopBarMenuButton()">
      <i class="pi pi-ellipsis-v"></i>
    </button>

    <router-link :to="{ name: 'CategoryViewAll' }" class="topbar-view-all-link">
      <span style="color: #1b2750; display: inline-block; margin-left: 1rem;">View all items</span>
    </router-link>

    <div class="header-logo">
      <router-link to="/auction/category/All" class="layout-topbar-logo">
        <img src="@/assets/tpwf-transparent-no-padding.png" alt="logo" />
      </router-link>
    </div>

    <div class="layout-topbar-menu" :class="topbarMenuClasses">
      <div v-if="userIsAdmin" class="mr-3">
        <router-link :to="{ name: 'Auction Items List' }">
          <button class="p-link layout-topbar-button">
            <i class="pi pi-lock"></i>
            <p class="pb-0 pt-3 pl-2">Admin</p>
          </button>
        </router-link>
      </div>

      <button
        v-if="userIsAdmin"
        class="p-link layout-topbar-button help-bell"
        :class="{ 'has-open-requests': openHelpCount > 0 }"
        @click="helpDialogVisible = true"
      >
        <div class="help-bell-icon-wrap">
          <i class="pi pi-bell"></i>
          <span v-if="openHelpCount > 0" class="help-count">{{ openHelpCount }}</span>
        </div>
        <span>Help</span>
      </button>

      <div v-if="isUserAuthenticated">
        <router-link :to="{ name: 'Edit User Info' }">
          <button @click="onSettingsClick()" class="p-link layout-topbar-button profile-user-button">
            <i class="pi pi-user"></i>
            <span>{{ displayName }}</span>
          </button>
        </router-link>
      </div>

      <button v-if="isUserAuthenticated && !userIsAdmin" @click="openHelpDialog" class="p-link layout-topbar-button">
        <i class="pi pi-question-circle"></i>
        <span>Need help</span>
      </button>

      <button v-if="isUserAuthenticated" @click="switchUser" class="p-link layout-topbar-button">
        <i class="pi pi-users"></i>
        <span>Switch user</span>
      </button>

      <button v-if="isUserAuthenticated && !isKiosk" @click="signOut" class="p-link layout-topbar-button">
        <i class="pi pi-sign-out"></i>
        <span>Sign out</span>
      </button>
    </div>

    <PrimeDialog v-model:visible="showHelpDialog" modal header="Need help?" :style="{ width: '30rem' }">
      <div class="help-dialog-body">
        <p class="help-small">Admin will receive a notification with your table and name.</p>

        <label class="help-label">Message (optional)</label>
        <textarea v-model="helpMessage" rows="4" class="form-control" placeholder="E.g. Help with bidding on item 104"></textarea>
      </div>

      <template #footer>
        <button type="button" class="btn btn-outline-secondary" @click="closeHelpDialog">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="sendingHelp" @click="submitHelpRequest">Send</button>
      </template>
    </PrimeDialog>

    <PrimeDialog v-model:visible="helpDialogVisible" modal header="Active help requests" :style="{ width: '42rem' }">
      <div v-if="openHelpRequests.length" class="help-list">
        <div
          v-for="request in openHelpRequests"
          :key="request.id"
          class="help-item"
        >
          <div class="help-item-head">
            <strong>{{ getRequestSummary(request) }}</strong>
            <small>{{ formatRequestTimestamp(request.createdAt) }}</small>
          </div>
          <div class="help-meta">
            <span class="badge bg-secondary">{{ request.type || 'General' }}</span>
          </div>
          <p class="help-message">{{ request.message }}</p>
          <button class="btn btn-sm btn-success" @click="markHelpResolved(request.id)">
            Mark as resolved
          </button>
        </div>
      </div>
      <p v-else class="mb-0">No active help requests right now.</p>
    </PrimeDialog>

    <Teleport to="body">
      <transition name="fade">
        <div v-if="showIdleWarning" class="idle-warning-overlay" @click="resetIdleTimer">
          <div class="idle-warning-box">
            <i class="pi pi-clock" style="font-size: 2rem; margin-bottom: 0.5rem;"></i>
            <p class="idle-warning-text">You will be signed out in <strong>{{ idleCountdown }}</strong> seconds</p>
            <p class="idle-warning-hint">Touch the screen to stay signed in</p>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.layout-topbar-button.profile-user-button {
  width: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.header-logo {
  align-items: center;
  justify-content: center;
  display: flex;
  flex: 1;
  min-width: 0;
}

.help-bell {
  position: relative;
}
.help-bell.has-open-requests .pi-bell {
  color: #dc2626;
}

.help-bell-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.help-count {
  position: absolute;
  top: -8px;
  right: -10px;
  background: #dc2626;
  color: #fff;
  border-radius: 999px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  font-size: 0.7rem;
  padding: 0 4px;
}

.help-list {
  display: grid;
  gap: 0.75rem;
}

.help-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: left;
}

.help-item-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.help-meta {
  margin-bottom: 0.4rem;
}

.help-message {
  margin-bottom: 0.5rem;
  color: #374151;
}

.help-dialog-body {
  text-align: left;
}

.help-small {
  font-size: 0.85rem;
  color: #6b7280;
}

.help-label {
  display: block;
  margin-bottom: 0.35rem;
  font-weight: 600;
}

</style>

<style lang="scss">
.idle-warning-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.idle-warning-box {
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 90vw;
}

.idle-warning-text {
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  color: #1b2750;
}

.idle-warning-hint {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
